import {
  alpha,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Iconify from "../iconify/Iconify";
import { SLOT_STATUS_META } from "../../pages/trainer/trainerUtils";
import TrainerSlotCard from "./TrainerSlotCard";

export default function TrainerMonthDetailsCard({
  desktop = false,
  selectedMonthDateValue,
  selectedMonthDateLabel,
  selectedMonthSlots,
  selectedMonthSiteCount,
  selectedMonthOverview,
  mobileFocusCue,
  mobileMonthDetailsRef,
  onOpenSlot,
}) {
  const theme = useTheme();
  const isMobileMonthFocused =
    !desktop &&
    selectedMonthDateValue &&
    mobileFocusCue === `month-${selectedMonthDateValue}`;

  if (!selectedMonthDateValue) {
    return (
      <Paper
        ref={!desktop ? mobileMonthDetailsRef : undefined}
        variant="outlined"
        sx={{
          p: desktop ? 3 : 2.5,
          borderRadius: 3,
          textAlign: "center",
          borderStyle: "dashed",
          scrollMarginTop: !desktop ? 88 : undefined,
          transition:
            "border-color 0.24s ease, box-shadow 0.24s ease, transform 0.24s ease",
          ...(isMobileMonthFocused
            ? {
                borderColor: alpha(theme.palette.primary.main, 0.34),
                boxShadow: `0 18px 36px ${alpha(
                  theme.palette.primary.main,
                  0.14,
                )}`,
                transform: "translateY(-2px)",
              }
            : {}),
        }}
      >
        <Iconify
          icon="mdi:gesture-tap-button"
          width={28}
          sx={{ color: "text.disabled", mb: 0.5 }}
        />
        <Typography variant="body2" color="text.secondary">
          {desktop
            ? "Wähle links einen Tag aus, um Trainings und Buchungsoptionen anzuzeigen."
            : "Tippe auf einen Tag, um die Trainings anzuzeigen."}
        </Typography>
      </Paper>
    );
  }

  const hasSlotsOnDay = selectedMonthSlots.length > 0;
  const actionHint = !hasSlotsOnDay
    ? "An diesem Tag ist kein Training geplant."
    : selectedMonthOverview.openCount > 0
    ? `${selectedMonthOverview.openCount} freie Trainings können direkt übernommen werden.`
    : selectedMonthOverview.mineCount > 0
    ? "Deine gebuchten Trainings für diesen Tag findest du direkt unten."
    : "Alle Trainings für diesen Tag sind bereits vergeben.";

  return (
    <Paper
      ref={!desktop ? mobileMonthDetailsRef : undefined}
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        borderColor: hasSlotsOnDay
          ? alpha(theme.palette.primary.main, 0.22)
          : alpha(theme.palette.divider, 0.7),
        display: "flex",
        flexDirection: "column",
        scrollMarginTop: !desktop ? 88 : undefined,
        transition:
          "border-color 0.24s ease, box-shadow 0.24s ease, transform 0.24s ease",
        ...(isMobileMonthFocused
          ? {
              borderColor: alpha(theme.palette.primary.main, 0.38),
              boxShadow: `0 20px 38px ${alpha(theme.palette.primary.main, 0.16)}`,
              transform: "translateY(-2px)",
            }
          : {}),
      }}
    >
      <Box
        sx={{
          px: desktop ? 2 : 1.5,
          py: desktop ? 1.75 : 1.3,
          background: hasSlotsOnDay
            ? `linear-gradient(160deg, ${alpha(
                theme.palette.primary.main,
                0.08,
              )}, ${alpha(theme.palette.primary.main, 0.02)})`
            : alpha(theme.palette.action.hover, 0.32),
        }}
      >
        <Stack spacing={desktop ? 1.25 : 1}>
          {desktop && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Tagesdetails
            </Typography>
          )}

          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant={desktop ? "subtitle1" : "subtitle2"}
                sx={{ fontWeight: 800 }}
              >
                {selectedMonthDateLabel}
              </Typography>
              <Typography
                variant={desktop ? "body2" : "caption"}
                color="text.secondary"
              >
                {selectedMonthOverview.summaryLabel}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={
                hasSlotsOnDay
                  ? `${selectedMonthOverview.bookedCount}/${selectedMonthOverview.totalCount} gebucht`
                  : "Leer"
              }
              sx={{
                height: 22,
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: hasSlotsOnDay
                  ? alpha(theme.palette.primary.main, 0.12)
                  : alpha(theme.palette.text.primary, 0.06),
                color: hasSlotsOnDay ? "primary.main" : "text.secondary",
              }}
            />
          </Stack>

          {desktop && hasSlotsOnDay && (
            <Stack direction="row" spacing={0.9}>
              <SummaryBox label="Trainings" value={selectedMonthOverview.totalCount} />
              <SummaryBox label="Standorte" value={selectedMonthSiteCount} />
              <SummaryBox
                label="Start"
                value={selectedMonthOverview.firstSlot?.session?.start || "--"}
              />
            </Stack>
          )}

          {hasSlotsOnDay && (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
              {selectedMonthOverview.openCount > 0 && (
                <StatusChip
                  label={`${selectedMonthOverview.openCount} offen`}
                  meta={SLOT_STATUS_META.open}
                />
              )}
              {selectedMonthOverview.mineCount > 0 && (
                <StatusChip
                  label={`${selectedMonthOverview.mineCount} meine`}
                  meta={SLOT_STATUS_META.mine}
                />
              )}
              {selectedMonthOverview.occupiedCount > 0 && (
                <StatusChip
                  label={`${selectedMonthOverview.occupiedCount} besetzt`}
                  meta={SLOT_STATUS_META.occupied}
                />
              )}
            </Stack>
          )}

          {desktop && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              sx={{
                p: 1.15,
                borderRadius: 2.25,
                border: "1px solid",
                borderColor: alpha(theme.palette.primary.main, 0.14),
                backgroundColor: alpha(theme.palette.background.paper, 0.7),
              }}
            >
              <Iconify
                icon={
                  hasSlotsOnDay
                    ? "mdi:cursor-default-click-outline"
                    : "mdi:calendar-blank-outline"
                }
                width={18}
                sx={{
                  color: hasSlotsOnDay ? "primary.main" : "text.disabled",
                  mt: 0.1,
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {actionHint}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ p: desktop ? 2 : 1.5, flex: 1 }}>
        {hasSlotsOnDay ? (
          <Stack spacing={1}>
            {selectedMonthSlots.map((slot) => (
              <TrainerSlotCard
                key={`${slot.siteId}-${slot.date}-${slot.sessionKey}`}
                slot={slot}
                onClick={onOpenSlot}
              />
            ))}
          </Stack>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              p: desktop ? 3 : 2.5,
              borderRadius: 2.5,
              textAlign: "center",
              borderStyle: "dashed",
            }}
          >
            <Iconify
              icon="mdi:calendar-blank-outline"
              width={28}
              sx={{ color: "text.disabled", mb: 0.5 }}
            />
            <Typography variant="body2" color="text.secondary">
              Kein Training an diesem Tag
            </Typography>
          </Paper>
        )}
      </Box>
    </Paper>
  );
}

function SummaryBox({ label, value }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1,
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(theme.palette.primary.main, 0.14),
        backgroundColor: alpha(theme.palette.background.paper, 0.74),
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
        {value}
      </Typography>
    </Box>
  );
}

function StatusChip({ label, meta }) {
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 22,
        fontSize: "0.68rem",
        fontWeight: 700,
        backgroundColor: meta.bg,
        color: meta.text,
      }}
    />
  );
}
